import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { notesCard } from "../style";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "../../../utils/hooks/useWindowSize";
import { Note, TripStop } from "../../../types/api";
import { CloseRounded } from "@mui/icons-material";
interface NotesCardProps {
  notes: Note[] | null;
  tripStops?: TripStop[] | null;
}

const NotesCard: React.FC<NotesCardProps> = ({ notes, tripStops }) => {
  const { t } = useTranslation();
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 700;

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Raggruppa le note per TMSLocation e trova la più recente con note
  const groupedNotes = React.useMemo(() => {
    const notesList = notes || [];
    const stopsList = tripStops || [];
    
    const grouped: {
      [tmsLocation: string]: {
        invPartyName: string;
        notaOrdine: string | null;
        notaDisposizione: string | null;
        mostRecentActivityDate: string | null;
      };
    } = {};

    // Raggruppa per TMSLocation e trova l'attività più recente con note
    stopsList.forEach(stop => {
      let notaDisposizione = null;
      let mostRecentActivityDate = null;
      
      if (stop.relatedActivities && stop.relatedActivities.length > 0) {
        // Trova l'attività con la data più recente che ha una nota
        const activitiesWithNotes = stop.relatedActivities
          .filter(activity => activity.activityRemark)
          .sort((a, b) => new Date(b.activityLastUpdate).getTime() - new Date(a.activityLastUpdate).getTime());
        
        if (activitiesWithNotes.length > 0) {
          notaDisposizione = activitiesWithNotes[0].activityRemark;
          mostRecentActivityDate = activitiesWithNotes[0].activityLastUpdate;
        }
      }

      grouped[stop.tmsLocation] = {
        invPartyName: stop.invPartyName,
        notaOrdine: null,
        notaDisposizione,
        mostRecentActivityDate
      };
    });

    // Aggiungi le note ordine per TMSLocation
    notesList.forEach(note => {
      if (grouped[note.tmsLocation] && grouped[note.tmsLocation].notaOrdine === null) {
        grouped[note.tmsLocation].notaOrdine = note.remark;
      }
    });

    return grouped;
  }, [notes, tripStops]);

  const groupedNotesList = Object.entries(groupedNotes).filter(([_, group]) => 
    group.notaOrdine || group.notaDisposizione
  );

  // Calcola il numero totale di note
  const totalNotesCount = React.useMemo(() => {
    return groupedNotesList.reduce((total, [_, group]) => {
      let count = 0;
      if (group.notaDisposizione) count++;
      if (group.notaOrdine) count++;
      return total + count;
    }, 0);
  }, [groupedNotesList]);

  // Trova il gruppo con l'attività più recente che ha note
  const mostRecentGroup = React.useMemo(() => {
    if (groupedNotesList.length === 0) return null;
    
    return groupedNotesList.reduce((mostRecent, [_, group]) => {
      if (!mostRecent) return group;
      
      // Se il gruppo corrente ha una data più recente e ha note, usalo
      if (group.mostRecentActivityDate && mostRecent.mostRecentActivityDate) {
        return new Date(group.mostRecentActivityDate) > new Date(mostRecent.mostRecentActivityDate) ? group : mostRecent;
      }
      
      // Se solo il gruppo corrente ha una data, usalo
      if (group.mostRecentActivityDate && !mostRecent.mostRecentActivityDate) {
        return group;
      }
      
      return mostRecent;
    }, null as any);
  }, [groupedNotesList]);

  return (
    <>
      <Card sx={notesCard.card}>
        <Box sx={notesCard.wrapper}>
          <Box sx={{display:'flex', columnGap:'8px', alignItems:'center'}}>
          <Typography sx={notesCard.title}>
            {t("screens.trips.notesCard.notes")}
          </Typography>
          {totalNotesCount > 0 && (
            <Box
              sx={{
                backgroundColor: "primary.main",
                borderRadius: "100px",
                display: "flex",
                flexDirection: "row",
                width: "18px",
                height:'18px',
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography sx={{ color: "white", fontSize: "14px",marginRight:'1px', fontWeight:'bold' }}>
                {totalNotesCount}
              </Typography>
            </Box>
            
          )}
          </Box>
          {!isMobile && (
            <Button
              variant="contained"
              onClick={handleDialogOpen}
              sx={notesCard.button}
              disabled={totalNotesCount === 0}
            >
              {t("screens.trips.notesCard.allNotes")}
            </Button>
          )}
        </Box>
        {totalNotesCount > 0 ? (
          <Box sx={{ marginTop: "16px" }}>
            <Typography sx={{ 
              fontSize: "14px", 
              fontWeight: "bold", 
              color: '#666',
              textTransform: 'uppercase',
              mb: 1
            }}>
              {mostRecentGroup.invPartyName}
            </Typography>
            {/* Mostra solo nota 1: se c'è nota disposizione, mostra quella, altrimenti mostra nota ordine come nota 1 */}
            {mostRecentGroup.notaOrdine ? (
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ 
                  fontSize: "14px", 
                  fontWeight: "bold", 
                  color: 'primary.main'
                }}>
                  {t('screens.trips.notesCard.note1')}: <span style={{ fontWeight: 'normal', color: 'black' }}>{mostRecentGroup.notaOrdine}</span>
                </Typography>
              </Box>
            ) : mostRecentGroup.notaDisposizione ? (
              <Box>
                <Typography sx={{ 
                  fontSize: "14px", 
                  fontWeight: "bold", 
                  color: 'primary.main'
                }}>
                  {t('screens.trips.notesCard.note1')}: <span style={{ fontWeight: 'normal', color: 'black' }}>{mostRecentGroup.notaDisposizione}</span>
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Typography sx={notesCard.text}>
            {t("screens.trips.notesCard.noNotes")}
          </Typography>
        )}
        {isMobile && (
          <Box sx={notesCard.mobileWrapper}>
            <Button
              variant="contained"
              onClick={handleDialogOpen}
              sx={notesCard.button}
              disabled={totalNotesCount === 0}
            >
              {t("screens.trips.notesCard.allNotes")}
            </Button>
          </Box>
        )}
      </Card>

      {/* Dialog per visualizzare tutte le note */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        sx={notesCard.dialog}
      >
        <DialogTitle sx={notesCard.dialogTitleBox}>
          <Box sx={notesCard.box}>
            <Typography sx={notesCard.text1}>
              {t("screens.trips.notesCard.notes")}
            </Typography>

            <IconButton onClick={handleDialogClose}>
              <CloseRounded />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={notesCard.wrapper1}>
          {totalNotesCount > 0 ? (
            <Box>
              {groupedNotesList.map(([tmsLocation, group], groupIndex) => (
                <Box key={tmsLocation} sx={{ mb: groupIndex < groupedNotesList.length - 1 ? 3 : 0 }}>
                  {/* Header del gruppo */}
                  <Box sx={{ 
                    backgroundColor: '#d0d0d0', 
                    padding: '8px 12px 8px 16px', 
                    marginBottom: '16px',
                    borderRadius: '0px'
                  }}>
                    <Typography sx={{ 
                      fontSize: "14px", 
                      fontWeight: "bold", 
                      color: 'black',
                      textTransform: 'uppercase'
                    }}>
                      {group.invPartyName}
                    </Typography>
                  </Box>
                  
                  {/* Note del gruppo */}
                  <Box>
                    {group.notaOrdine && (
                      <Box sx={{ mb: 2, pl: 2 }}>
                        <Typography sx={{ 
                          fontSize: "14px", 
                          fontWeight: "bold", 
                          color: 'primary.main',
                          mb: 1
                        }}>
                          {t('screens.trips.notesCard.note1')}: <span style={{ fontWeight: 'normal', color: 'black' }}>{group.notaOrdine}</span>
                        </Typography>
                      </Box>
                    )}
                    {group.notaOrdine && group.notaDisposizione && (
                      <Box sx={{ borderBottom: '1px solid #e0e0e0', mb: 2 }} />
                    )}
                    {group.notaDisposizione && (
                      <Box sx={{ pl: 2 }}>
                        <Typography sx={{ 
                          fontSize: "14px", 
                          fontWeight: "bold", 
                          color: 'primary.main',
                          mb: 1
                        }}>
                          {group.notaOrdine ? t('screens.trips.notesCard.note2') : t('screens.trips.notesCard.note1')}: <span style={{ fontWeight: 'normal', color: 'black' }}>{group.notaDisposizione}</span>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography>{t("screens.trips.notesCard.noNotes")}</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotesCard;
